import { useNavigate } from "react-router-dom";
import { ArrowLeft, Clock, TrendingDown, TrendingUp, Timer, CalendarIcon } from "lucide-react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { cn } from "@/lib/utils";
import {
  useKdsPerformanceReport,
  formatDuration,
  type DatePreset,
  type KdsPerformanceItem,
} from "@/hooks/useKdsPerformanceReport";
import { useProductionLocations } from "@/hooks/useProductionLocations";
import {
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Button } from "@/components/ui/button";

const SLA_WARNING = 600; // 10min
const SLA_CRITICAL = 1200; // 20min

function getSlaColor(seconds: number) {
  if (seconds >= SLA_CRITICAL) return "text-red-400";
  if (seconds >= SLA_WARNING) return "text-amber-400";
  return "text-emerald-400";
}

function getSlaLabel(seconds: number) {
  if (seconds >= SLA_CRITICAL) return "Crítico";
  if (seconds >= SLA_WARNING) return "Alerta";
  return "OK";
}

function getLocationLabel(item: KdsPerformanceItem) {
  if (item.tableNumber) return `Mesa ${item.tableNumber}`;
  if (item.tabNumber) return `Cmd ${item.tabNumber}`;
  if (item.customerName) return item.customerName;
  return "—";
}

export default function KdsDashboardScreen() {
  const navigate = useNavigate();
  const { locations } = useProductionLocations();
  const {
    items,
    summary,
    isLoading,
    datePreset,
    setDatePreset,
    customRange,
    setCustomRange,
    locationId,
    setLocationId,
  } = useKdsPerformanceReport();

  const presets: { value: DatePreset; label: string }[] = [
    { value: "today", label: "Hoje" },
    { value: "yesterday", label: "Ontem" },
    { value: "last7", label: "Últimos 7 dias" },
    { value: "custom", label: "Personalizado" },
  ];

  return (
    <div className="h-full flex flex-col bg-zinc-900 text-white overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-800">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate("/kds")}
            className="p-2 rounded-lg bg-zinc-800 hover:bg-zinc-700 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="text-xl font-bold">Dashboard de Produção</h1>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-3">
          <Select value={datePreset} onValueChange={(v) => setDatePreset(v as DatePreset)}>
            <SelectTrigger className="w-[160px] bg-zinc-800 border-zinc-700 text-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {presets.map((p) => (
                <SelectItem key={p.value} value={p.value}>
                  {p.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {datePreset === "custom" && (
            <div className="flex items-center gap-2">
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" size="sm" className="bg-zinc-800 border-zinc-700 text-white">
                    <CalendarIcon className="w-4 h-4 mr-1" />
                    {format(customRange.from, "dd/MM", { locale: ptBR })}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={customRange.from}
                    onSelect={(d) => d && setCustomRange((prev) => ({ ...prev, from: d }))}
                    className="p-3 pointer-events-auto"
                  />
                </PopoverContent>
              </Popover>
              <span className="text-zinc-500">até</span>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" size="sm" className="bg-zinc-800 border-zinc-700 text-white">
                    <CalendarIcon className="w-4 h-4 mr-1" />
                    {format(customRange.to, "dd/MM", { locale: ptBR })}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={customRange.to}
                    onSelect={(d) => d && setCustomRange((prev) => ({ ...prev, to: d }))}
                    className="p-3 pointer-events-auto"
                  />
                </PopoverContent>
              </Popover>
            </div>
          )}

          <Select value={locationId} onValueChange={setLocationId}>
            <SelectTrigger className="w-[180px] bg-zinc-800 border-zinc-700 text-white">
              <SelectValue placeholder="Todos os locais" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos os locais</SelectItem>
              {locations
                .filter((l) => l.is_active)
                .map((l) => (
                  <SelectItem key={l.id} value={l.id}>
                    {l.name}
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 px-6 py-5">
        <div className="rounded-xl bg-zinc-800 border border-zinc-700 p-5">
          <div className="flex items-center gap-2 text-zinc-400 text-sm mb-1">
            <Clock className="w-4 h-4" />
            Tempo Médio
          </div>
          <p className="text-3xl font-bold text-white">
            {summary.totalItems > 0 ? formatDuration(summary.avgSeconds) : "--:--"}
          </p>
          <p className="text-xs text-zinc-500 mt-1">{summary.totalItems} itens produzidos</p>
        </div>

        <div className="rounded-xl bg-zinc-800 border border-emerald-800/50 p-5">
          <div className="flex items-center gap-2 text-emerald-400 text-sm mb-1">
            <TrendingDown className="w-4 h-4" />
            Melhor Tempo
          </div>
          <p className="text-3xl font-bold text-emerald-400">
            {summary.bestItem ? formatDuration(summary.bestItem.durationSeconds) : "--:--"}
          </p>
          {summary.bestItem && (
            <p className="text-xs text-zinc-500 mt-1 truncate">
              {summary.bestItem.nameSnapshot} • {getLocationLabel(summary.bestItem)}
            </p>
          )}
        </div>

        <div className="rounded-xl bg-zinc-800 border border-red-800/50 p-5">
          <div className="flex items-center gap-2 text-red-400 text-sm mb-1">
            <TrendingUp className="w-4 h-4" />
            Pior Tempo
          </div>
          <p className="text-3xl font-bold text-red-400">
            {summary.worstItem ? formatDuration(summary.worstItem.durationSeconds) : "--:--"}
          </p>
          {summary.worstItem && (
            <p className="text-xs text-zinc-500 mt-1 truncate">
              {summary.worstItem.nameSnapshot} • {getLocationLabel(summary.worstItem)}
            </p>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="flex-1 overflow-auto px-6 pb-6">
        {isLoading ? (
          <div className="flex items-center justify-center h-40 text-zinc-500">
            <Timer className="w-5 h-5 animate-spin mr-2" />
            Carregando dados...
          </div>
        ) : items.length === 0 ? (
          <div className="flex items-center justify-center h-40 text-zinc-500">
            Nenhum item finalizado no período selecionado.
          </div>
        ) : (
          <div className="rounded-xl border border-zinc-800 overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="border-zinc-800 hover:bg-transparent">
                  <TableHead className="text-zinc-400">Pedido</TableHead>
                  <TableHead className="text-zinc-400">Mesa / Comanda</TableHead>
                  <TableHead className="text-zinc-400">Produto</TableHead>
                  <TableHead className="text-zinc-400 text-center">Qtd</TableHead>
                  <TableHead className="text-zinc-400 text-center">Tempo</TableHead>
                  <TableHead className="text-zinc-400 text-center">Entrada</TableHead>
                  <TableHead className="text-zinc-400 text-center">Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((item) => (
                  <TableRow key={item.id} className="border-zinc-800 hover:bg-zinc-800/50">
                    <TableCell className="text-white font-medium">
                      #{item.orderNumber ?? "—"}
                    </TableCell>
                    <TableCell className="text-zinc-300">{getLocationLabel(item)}</TableCell>
                    <TableCell className="text-white">{item.nameSnapshot}</TableCell>
                    <TableCell className="text-center text-zinc-300">{item.quantity}</TableCell>
                    <TableCell
                      className={cn("text-center font-mono font-semibold", getSlaColor(item.durationSeconds))}
                    >
                      {formatDuration(item.durationSeconds)}
                    </TableCell>
                    <TableCell className="text-center text-zinc-400 text-sm">
                      {format(new Date(item.openedAt), "HH:mm", { locale: ptBR })}
                    </TableCell>
                    <TableCell className="text-center">
                      <span
                        className={cn(
                          "px-2 py-0.5 rounded-full text-xs font-medium",
                          item.durationSeconds >= SLA_CRITICAL
                            ? "bg-red-500/20 text-red-400"
                            : item.durationSeconds >= SLA_WARNING
                            ? "bg-amber-500/20 text-amber-400"
                            : "bg-emerald-500/20 text-emerald-400"
                        )}
                      >
                        {getSlaLabel(item.durationSeconds)}
                      </span>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </div>
  );
}
